<script lang="ts" setup>
import type { SerializedEditorState } from "lexical";
import { computed, inject, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { useRouter } from "vue-router";

import actions from "~/actions";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import TaskOrDocDropdownItem from "~/components/dumb/TaskOrDocDropdownItem.vue";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import { MergeIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { Placement, PropertyKind, RelationshipKindKind, StatusKind } from "~/shared/enums";
import type { PropertyValue, Task } from "~/shared/types";
import { useAppStore, useDataStore, useUserStore } from "~/stores";
import { getTaskLink, isLexicalStateEmpty } from "~/utils/common";
import { sign } from "~/utils/comparator";
import { useAsyncTasks, usePaginatedTasksWithSearch } from "~/utils/entityIncrementalLoading";
import { timeout } from "~/utils/wait";

const props = defineProps<{
  targetTask: Task;
  selectedTasks: Task[];
  onAfterClose?: () => void;
  itemIndex?: number;
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const userStore = useUserStore();

const menu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const scrollContainer = computed(() => menu.value?.scrollContainer ?? null);

const isOpen = computed(() => menu.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const search = ref("");
const pageDuid = computed(() => `task-dropdown-${search.value}`);

const { searchedTasks, isLoading, reset: resetTasks } = usePaginatedTasksWithSearch(pageDuid, scrollContainer, search);

const { tasks: recentTasks } = useAsyncTasks(
  computed(() => userStore.recentTaskStack),
  computed(() => ({ includeTrashed: false, includeDraft: false }))
);

const tasksToMerge = computed(() => (props.selectedTasks.length > 1 ? props.selectedTasks : [props.targetTask]));

const createTaskItem = (task: Task, customKey?: string) => {
  const selected = props.selectedTasks.some((selectedTask) => selectedTask.duid === task.duid);
  return {
    value: task.duid,
    label: task.title,
    selected,
    disabled: false,
    customKey,
    component: TaskOrDocDropdownItem,
    componentArgs: {
      task,
      label: task.title,
      selected,
      disabled: false,
      lineThrough: false,
      showOpen: true,
      onOpen: () => {
        router.push(getTaskLink(task));
      },
    },
  };
};

const taskSections = computed(() => {
  const recentTaskCount = userStore.recentTaskStack.length;
  const excludedDuids = new Set(tasksToMerge.value.map((task) => task.duid));
  const taskList = [...searchedTasks.value].filter((task) => !excludedDuids.has(task.duid));

  const sortedEntries = actions.task.groupAndSortTasksByDartboard(taskList, props.targetTask.dartboardDuid);

  const recentSection =
    userStore.recentTaskStack.length > 0 && recentTasks.value.length > 0
      ? [
          {
            title: "Recent",
            showTitle: true,
            items: recentTasks.value
              .filter((task) => !excludedDuids.has(task.duid))
              .map((task) => createTaskItem(task, `recent/${task.duid}`)),
          },
        ]
      : [];

  const regularSections = sortedEntries.map(([title, tasks]) => ({
    title,
    showTitle: true,
    items: tasks
      .sort((a, b) => {
        const aIndex = userStore.recentTaskStack.indexOf(a.duid);
        const bIndex = userStore.recentTaskStack.indexOf(b.duid);
        return sign((aIndex > -1 ? aIndex : recentTaskCount) - (bIndex > -1 ? bIndex : recentTaskCount));
      })
      .map((task) => createTaskItem(task)),
  }));

  return [...recentSection, ...regularSections];
});

const mergeDescriptions = (
  targetDescription: SerializedEditorState,
  sourceDescription: SerializedEditorState
): SerializedEditorState => {
  if (isLexicalStateEmpty(sourceDescription)) {
    return targetDescription;
  }
  if (isLexicalStateEmpty(targetDescription)) {
    return sourceDescription;
  }
  const targetChildren = targetDescription.root.children;
  const sourceChildren = sourceDescription.root.children;
  const divider = { type: "horizontal-rule", version: 1 };
  return {
    ...targetDescription,
    root: {
      ...targetDescription.root,
      children: [...targetChildren, divider, ...sourceChildren],
    },
  };
};

const mergeLinks = (targetTaskDuid: string, sourceTask: Task) => {
  sourceTask.links.forEach((link) => dataStore.addLink(targetTaskDuid, link.kind, link.url, link.title || null));
};

const mergeProperties = (targetTask: Task, sourceTask: Task): Record<string, PropertyValue> =>
  Object.entries(sourceTask.properties).reduce(
    (acc, [key, value]) => {
      const property = dataStore.getPropertyByDuid(key);
      if (!property) {
        return acc;
      }
      if (property.kind === PropertyKind.MULTISELECT || property.kind === PropertyKind.USER) {
        // oxlint-disable-next-line no-param-reassign
        acc[key] = [...new Set([...(acc[key] as string[]), ...(value as string[])])];
        return acc;
      }
      if (!(key in acc) || acc[key] === null) {
        // oxlint-disable-next-line no-param-reassign
        acc[key] = value;
      }
      return acc;
    },
    { ...targetTask.properties }
  );

const mergeRelationships = async (targetTask: Task, sourceTask: Task): Promise<void> => {
  const parentKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
  const sourceTaskSubtasks = dataStore.getRelationshipsByKindKind(sourceTask, RelationshipKindKind.PARENT_OF, true);

  if (sourceTaskSubtasks.length > 0) {
    const subtasks = await dataStore.getTasksByDuids(
      sourceTaskSubtasks.map((e) => e.targetDuid),
      { includeTrashed: false, includeDraft: false }
    );
    const tasksAndUpdates = sourceTaskSubtasks
      .map((relationship) => {
        const task = subtasks.find((e) => e.duid === relationship.targetDuid);
        if (!task) {
          return undefined;
        }
        return { task, order: task.order };
      })
      .filter(isDefined);

    dataStore.changeParent(tasksAndUpdates, targetTask);
  }

  sourceTask.relationships.forEach((relationship) => {
    if (relationship.kindDuid === parentKindDuid) {
      return;
    }
    // TODO handle loops
    dataStore.createRelationship(targetTask.duid, relationship.targetDuid, relationship.kindDuid);
  });
};

const mergeTasks = async (targetTask: Task, sourceTask: Task) => {
  const mergedTitle = targetTask.title.trim() || sourceTask.title;
  const mergedSubscriberDuids = [...new Set([...targetTask.subscriberDuids, ...sourceTask.subscriberDuids])];
  const mergedTagDuids = [...new Set([...targetTask.tagDuids, ...sourceTask.tagDuids])];
  // TODO replicate the attachments in the target
  const mergedAttachmentDuids = [...new Set([...targetTask.attachmentDuids, ...sourceTask.attachmentDuids])];

  mergeLinks(targetTask.duid, sourceTask);
  await mergeRelationships(targetTask, sourceTask);

  const mergedProperties = mergeProperties(targetTask, sourceTask);

  const canceledStatusMaybe = dataStore.getStatusesByKinds([StatusKind.CANCELED], dataStore.defaultStatusProperty)[0];
  const newStatus = canceledStatusMaybe ?? dataStore.defaultDefaultFinishedStatus;

  await dataStore.updateTasks([
    {
      duid: targetTask.duid,
      title: mergedTitle,
      subscriberDuids: mergedSubscriberDuids,
      tagDuids: mergedTagDuids,
      attachmentDuids: mergedAttachmentDuids,
      properties: mergedProperties,
    },
    {
      duid: sourceTask.duid,
      statusDuid: newStatus.duid,
    },
  ]);
};

const onSelect = async (taskDuid: string) => {
  const targetTask = await dataStore.getTaskByDuid(taskDuid);
  if (!targetTask) {
    return;
  }

  const relatesToRelationshipKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.DUPLICATES).duid;

  // TODO transactionify
  const sourceTasks = tasksToMerge.value.filter((task) => task.duid !== taskDuid);

  await Promise.all(
    sourceTasks.map(async (sourceTask) => {
      await mergeTasks(targetTask, sourceTask);
      dataStore.createRelationship(sourceTask.duid, targetTask.duid, relatesToRelationshipKindDuid);
    })
  );

  dataStore.deleteTasks(sourceTasks);

  await actions.visualization.navigateToTask(taskDuid);
  for (let i = 0; i < 10; i += 1) {
    // oxlint-disable-next-line no-await-in-loop
    await timeout(100);
    if (appStore.taskDetail?.isDescriptionReady) {
      break;
    }
  }
  if (!appStore.taskDetail || !appStore.taskDetail.isDescriptionReady) {
    return;
  }

  const allDescriptions = [targetTask, ...sourceTasks].map((task) => task.description);
  const finalDescription = allDescriptions.reduce((acc, desc) => mergeDescriptions(acc, desc), targetTask.description);
  appStore.taskDetail.setDescription(finalDescription);
};

const onSearchTasks = (value: string) => {
  search.value = value;
  searchedTasks.value = [];
  resetTasks();
};
</script>

<template>
  <MultiselectDropdownMenu
    ref="menu"
    :sections="taskSections"
    header-title="Merge into other task"
    placeholder="Merge into task..."
    :placement="Placement.RIGHT_TOP"
    :width-pixels="448"
    :on-after-close="onAfterClose"
    close-on-select
    infinite-list
    :loading-more="isLoading"
    @search="onSearchTasks"
    @add="onSelect">
    <DropdownMenuItemContent :icon="MergeIcon" title="Merge into other task" is-submenu />
  </MultiselectDropdownMenu>
</template>
